import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// While the backend restarts during a deploy (a few seconds), nginx answers 502 with its own HTML
// page or the connection drops. Read requests are retried quietly so users never see that window.
// Only that signature is retried: the app's own 503s (DB errors, rate limiting) are JSON, and a 504
// means a slow request that is still running, so repeating it would only multiply the load.
// Writes are never retried: repeating a POST could send a message or create a record twice.

const SAFE_METHODS = new Set(['get', 'head', 'options']);
const DELAYS_MS = [1000, 2000, 3000, 4000, 5000];

type RetryConfig = InternalAxiosRequestConfig & { _restartRetry?: number };

export function installRestartRetry(instance: AxiosInstance): void {
  instance.interceptors.response.use(undefined, async (error: AxiosError) => {
    const config = error.config as RetryConfig | undefined;
    const method = (config?.method || 'get').toLowerCase();
    const status = error.response?.status;
    const proxyPage = typeof error.response?.data === 'string';
    const retryable = status ? status === 502 && proxyPage : error.code === 'ERR_NETWORK';
    const attempt = config?._restartRetry || 0;

    if (!config || !SAFE_METHODS.has(method) || !retryable || attempt >= DELAYS_MS.length || axios.isCancel(error)) {
      return Promise.reject(error);
    }
    config._restartRetry = attempt + 1;
    await new Promise(resolve => setTimeout(resolve, DELAYS_MS[attempt]));
    return instance.request(config);
  });
}
